import type {Action} from "../../store/features/CallQueue/CallQueueService";
import {omit} from "lodash";
import {digitsOf} from "../../utils/callRestrictions/apiErrors";
import {removeEmptyFields} from "../../utilis/removeEmptyFields";

/** Form state of the queue drawer (same shape the API takes, plus UI-only labels). */
export type QueueFormValues = {
    name: string;
    ringStrategy: string;
    ivrMenuOption: {
        maxMenuRepeats: number;
        enabled: boolean;
        actions: Action[];
        messageSettings: {
            WELCOME_MESSAGE: { originalFileName: string; bucketFileName: string; tts?: unknown };
            MENU_MESSAGE: { originalFileName: string; bucketFileName: string; tts?: unknown };
        };
    };
};

export const STRATEGIES = [
    {value: 'RING_ALL', label: 'Ring all', description: 'Call all agents at once. First to answer handles the call.'},
    {value: 'LONGEST_IDLE_AGENT', label: 'Longest idle', description: 'Call the agent idle the longest, considering tiers.'},
    {value: 'RANDOM', label: 'Random', description: 'Call agents in random order.'},
] as const;

export const strategyLabel = (value?: string) => STRATEGIES.find((s) => s.value === value)?.label ?? 'Ring all';

export const KEY_NAME_MAX = 12;
export const MENU_REPEATS_MAX = 9;

/** Keys 1–9 not used by another option; `current` stays available to its own option. */
export const availableKeys = (actions: Pick<Action, 'order'>[], current?: string): string[] => {
    const used = new Set(actions.map((a) => String(a.order)));
    return ['1', '2', '3', '4', '5', '6', '7', '8', '9'].filter((k) => k === current || !used.has(k));
};

/** The key a new option gets: the lowest free one, or null when all nine are taken. */
export const nextFreeKey = (actions: Pick<Action, 'order'>[]): string | null => availableKeys(actions)[0] ?? null;

export const emptyQueue = (): QueueFormValues => ({
    name: '',
    ringStrategy: 'RING_ALL',
    ivrMenuOption: {
        enabled: false,
        maxMenuRepeats: 3,
        // Key 0 always exists: it takes callers who press nothing else.
        actions: [{type: 'Receive', order: '0', keyName: ''}],
        messageSettings: {
            WELCOME_MESSAGE: {originalFileName: '', bucketFileName: ''},
            MENU_MESSAGE: {originalFileName: '', bucketFileName: ''},
        },
    },
});

export type DestinationKind =
    | 'Receive'
    | 'ForwardToSpecificUser'
    | 'ForwardToGroup'
    | 'ForwardToInternalNumber'
    | 'ForwardToExternalNumber'
    | 'PlayMessage';

interface Named { value: string; label: string }

/** What the Destination field shows for an option. */
export const destinationLabel = (
    action: Action,
    lists: { users: Named[]; groups: Named[]; numbers: Named[] },
    formatExternal: (phoneNumber: string) => string,
): string | null => {
    const find = (items: Named[], id?: number) => items.find((i) => i.value === String(id))?.label;
    switch (action.type) {
        case 'Receive':
            return 'Receive call';
        case 'ForwardToSpecificUser':
            return `Extension · ${find(lists.users, action.userId) ?? 'Unknown user'}`;
        case 'ForwardToGroup':
            return `Group · ${find(lists.groups, action.groupId) ?? 'Unknown group'}`;
        case 'ForwardToInternalNumber':
            return `Number · ${find(lists.numbers, action.phoneNumberId) ?? 'Unknown number'}`;
        case 'ForwardToExternalNumber':
            return action.phoneNumber ? `External · ${formatExternal(action.phoneNumber)}` : null;
        case 'PlayMessage':
            return 'Play message, then menu';
        default:
            return null;
    }
};

export type QueueErrors = Partial<Record<'name' | 'menuMessage', string>> & { keys?: Record<number, string> };

/** Client-side checks before saving; existing names come from the queue list. */
export const validateQueue = (values: QueueFormValues, existingNames: { id: string | null; name: string }[], ownId?: string): QueueErrors => {
    const errors: QueueErrors = {};
    const name = values.name.trim();
    if (!name) errors.name = 'Enter a name, e.g. Support Line, Sales Queue';
    else if (existingNames.some((q) => q.name.trim().toLowerCase() === name.toLowerCase() && String(q.id) !== String(ownId))) {
        errors.name = 'A queue with this name already exists.';
    }
    if (values.ivrMenuOption.enabled) {
        if (!values.ivrMenuOption.messageSettings.MENU_MESSAGE.originalFileName) {
            errors.menuMessage = 'Add a menu message so callers know which key to press.';
        }
        const keys: Record<number, string> = {};
        values.ivrMenuOption.actions.forEach((a, i) => {
            if (!a.type) keys[i] = 'Choose a destination.';
            else if (!a.keyName?.trim()) keys[i] = 'Name this option.';
        });
        if (Object.keys(keys).length) errors.keys = keys;
    }
    return errors;
};

export const hasErrors = (errors: QueueErrors) => Boolean(errors.name || errors.menuMessage || (errors.keys && Object.keys(errors.keys).length));

/** Request body for create/update: drops UI labels, empty fields and the menu when it is off. */
export const toQueuePayload = (values: QueueFormValues) => {
    const data = removeEmptyFields({
        ...values,
        name: values.name.trim(),
        ivrMenuOption: {
            ...values.ivrMenuOption,
            actions: values.ivrMenuOption.actions.map((a) => omit(a, 'label')),
        },
    });
    if (!values.ivrMenuOption.enabled) delete data.ivrMenuOption;
    return data;
};

export const externalTargets = (actions: Action[], enabled: boolean): Record<string, string | undefined> =>
    enabled
        ? Object.fromEntries(actions
            .map((action, index) => [String(index), action.type === 'ForwardToExternalNumber' ? action.phoneNumber : undefined] as const)
            .filter(([, number]) => number !== undefined))
        : {};

export const refusedOptionIndexes = (values: QueueFormValues, refusedNumbers: string[]): number[] => {
    const {enabled, actions} = values.ivrMenuOption;
    if (!enabled || !refusedNumbers.length) return [];
    return actions.flatMap((action, index) =>
        action.type === 'ForwardToExternalNumber' && refusedNumbers.includes(digitsOf(action.phoneNumber)) ? [index] : []);
};
